import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  Form,
  Select,
  Divider,
  Row,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getProperty } from '../../helpers/helpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import SimpleTextInputModal from '../../common/modals/SimpleTextInputModal';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import {
  addUserCustomExportsFilterView,
  deleteUserCustomExportsFilterView,
  updateUserCustomExportsFilterView,
} from '../state/reports.actions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  TIME_TRACKING_FILTER_VIEW_FIELDS,
  FORM_FILTER_VIEW_FIELDS,
  TIME_TRACKING_GRID_EXTRA_FILTER_VIEW_FIELDS,
} from './exports.constants';
import { getProjectOptions } from '../../clock/ManualEntry/manualEntryHelpers';
import { exportHasTimeTrackingGridColumns } from './exports.helpers';

import CustomExportTimeTrackingFilters from './CustomExportTimeTrackingFilters';
import CustomExportFormFilters from './CustomExportFormFilters';

const getFilterViewFields = (exportType, columns = []) => {
  if (exportType === 'timeEntries') return TIME_TRACKING_FILTER_VIEW_FIELDS;
  if (exportType === 'forms') {
    if (exportHasTimeTrackingGridColumns(columns)) {
      return [
        ...FORM_FILTER_VIEW_FIELDS,
        ...TIME_TRACKING_GRID_EXTRA_FILTER_VIEW_FIELDS,
      ];
    }
    return FORM_FILTER_VIEW_FIELDS;
  }
  return [];
};

const defaultTimeValues = {
  costcodes: [],
  timeTypes: [],
};

const defaultFormValues = {
  customers: [],
  equipment: [],
  buckets: [],
};

const defaultValues = {
  users: [],
  projects: [],
};

export default function CustomExportFilterTab({
  onClose,
  filterView = {},
  setActiveTab,
  selectedExport,
  onApplyFilter,
  activeFilters,
}) {
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const { settings = {} } = useSelector((state) => state.settings.company);
  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);
  const projects = useSelector((state) => state.projects.projects);

  const ourDivision = divisions[selectedExport?.divisionId];
  const userOptions = useMemo(() => (
    users
      .filter((user) => user.active && ourDivision?.users?.has(user.id))
      .map((user) => ({ label: user.name, value: user.id }))
  ), [users, ourDivision]);

  // Both
  const [selectedProjects, setSelectedProjects] = useState([]);

  // Time Entries
  const [selectedCostcodes, setSelectedCostcodes] = useState([]);
  const [selectedTimeTypes, setSelectedTimeTypes] = useState([]);

  // Forms
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedBuckets, setSelectedBuckets] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const currentFilters = useMemo(() => {
    if (!filterView || activeFilters.filterView === filterView.id) {
      return activeFilters;
    }
    return filterView;
  }, [filterView, activeFilters]);

  const hasTimeTrackingGridExportColumns = useMemo(() => (
    selectedExport?.type === 'timeEntries'
    || (
      selectedExport?.type === 'forms'
      && exportHasTimeTrackingGridColumns(selectedExport?.columns)
    )
  ), [selectedExport]);

  const formTitle = useMemo(() => {
    if (filterView) {
      return `Edit ${filterView.filterViewName} View`;
    }

    return 'Custom Filter';
  }, [filterView]);

  // ----------------
  // Dropdown Options
  // ----------------
  const projectOptions = useMemo(() => {
    const activeProjects = projects.filter(({ active }) => active);

    return getProjectOptions({
      activeProjects,
      divisionIds: [selectedExport?.divisionId],
      settings,
    });
  }, [projects, selectedExport]);

  const resetTimeFields = () => {
    form.setFieldsValue(defaultTimeValues);
    setSelectedProjects([]);
    setSelectedCostcodes([]);
  };

  const resetFormFields = () => {
    form.setFieldsValue(defaultFormValues);
  };

  const resetFields = () => {
    form.setFieldsValue(defaultValues);
    resetTimeFields();
    resetFormFields();
  };

  useEffect(() => {
    if (form) {
      form.setFieldsValue({
        ...defaultValues,
        filterViewName: getProperty(currentFilters, 'filterViewName', ''),
        users: getProperty(currentFilters, 'users', []),
        projects: getProperty(currentFilters, 'projects', []),
        costcodes: getProperty(currentFilters, 'costcodes', []),
        timeTypes: getProperty(currentFilters, 'timeTypes', []),
        customers: getProperty(currentFilters, 'customers', []),
        equipment: getProperty(currentFilters, 'equipment', []),
        buckets: getProperty(currentFilters, 'buckets', []),
      });

      setSelectedProjects(
        getProperty(currentFilters, 'projects', []),
      );
      setSelectedCostcodes(
        getProperty(currentFilters, 'costcodes', []),
      );
      setSelectedTimeTypes(
        getProperty(currentFilters, 'timeTypes', []),
      );
      setSelectedCustomers(
        getProperty(currentFilters, 'customers', []),
      );
      setSelectedEquipment(
        getProperty(currentFilters, 'equipment', []),
      );
      setSelectedBuckets(
        getProperty(currentFilters, 'buckets', []),
      );
    }

    return () => {
      resetFields();
    };
  }, [activeFilters, filterView]);

  const onSelectChange = useCallback((option) => (val) => {
    form.setFieldsValue({
      [option]: val,
    });

    switch (option) {
      case 'projects': {
        setSelectedProjects(val);
        break;
      }
      case 'costcodes': {
        setSelectedCostcodes(val);
        break;
      }
      case 'timeTypes': {
        setSelectedTimeTypes(val);
        break;
      }
      case 'customers': {
        setSelectedCustomers(val);
        break;
      }
      case 'equipment': {
        setSelectedEquipment(val);
        break;
      }
      case 'buckets': {
        setSelectedBuckets(val);
        break;
      }
      default: {
        break;
      }
    }
  }, [form]);

  const onApply = useCallback(() => {
    const values = form.getFieldsValue();

    if (filterView) {
      values.filterView = filterView.id;
    } else {
      values.filterView = null;
      values.filterViewName = null;
    }

    onApplyFilter(values);
    onClose();
  }, [filterView, onApplyFilter]);

  const onSave = useCallback(() => {
    const values = form.getFieldsValue();
    if (filterView) {
      const ourFields = getFilterViewFields(
        selectedExport?.type,
        selectedExport?.columns,
      );
      dispatch(updateUserCustomExportsFilterView(filterView.id, {
        name: values.filterViewName,
        filters: ourFields.map(({ field, defaultValue }) => ({
          filterProperty: field,
          filterValue: JSON.stringify(values[field] || defaultValue),
          filterViewId: filterView.id,
        })),
      }));
      onApply();
    } else {
      setIsModalOpen(true);
    }
  }, [
    filterView,
    activeFilters,
    selectedExport,
  ]);

  const onDelete = useCallback(() => new Promise((resolve) => {
    CustomConfirmModal({
      title: 'Delete Filter View',
      content: (
        <p>
          Are you sure you wish to delete this filter view?
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        resolve(await dispatch(
          deleteUserCustomExportsFilterView(filterView.id),
        ));

        if (activeFilters.filterView === filterView.id) {
          onApplyFilter();
        } else {
          setActiveTab(activeFilters.filterView || 'custom');
        }

        setIsModalOpen(false);
      },
      onCancel() {
        resolve();
      },
    });
  }), [
    activeFilters,
    filterView,
    onClose,
    onApplyFilter,
  ]);

  const addNewFilterView = useCallback(async (filterViewName) => {
    const values = form.getFieldsValue();
    const ourFields = getFilterViewFields(
      selectedExport?.type,
      selectedExport?.columns,
    );
    const {
      id,
    } = await dispatch(addUserCustomExportsFilterView({
      name: filterViewName,
      filters: ourFields.map(({ field, defaultValue }) => ({
        filterProperty: field,
        filterValue: JSON.stringify(values[field] || defaultValue),
      })),
      exportId: selectedExport?.id,
    }));

    onApply();
    setActiveTab(id);
  }, [
    form,
    onClose,
    setActiveTab,
    selectedExport,
  ]);

  return (
    <>
      <div>
        <Row>
          <h3>{formTitle}</h3>
          <OnTraccrButton
            onClick={resetFields}
            style={{ marginLeft: 'auto' }}
            title="Reset"
          />
        </Row>
        <Form
          form={form}
          layout="vertical"
        >
          {
            filterView
              && (
                <>
                  <Form.Item
                    name="filterViewName"
                    label="Filter View Name"
                    rules={[
                      { required: true, message: 'Filter View Name is required' },
                    ]}
                  >
                    <OnTraccrTextInput
                      style={{ width: '100%' }}
                      placeholder="Name"
                    />
                  </Form.Item>
                  <Divider />
                </>
              )
            }

          <Form.Item
            name="users"
            label="Users"
          >
            <Select
              mode="multiple"
              onChange={onSelectChange('users')}
              allowClear
              options={userOptions}
              placeholder="Users"
              optionFilterProp="label"
            />
          </Form.Item>

          { selectedExport?.type !== 'boardCards' && (
            <Form.Item
              name="projects"
              label="Projects"
            >
              <Select
                mode="multiple"
                onChange={onSelectChange('projects')}
                allowClear
                options={projectOptions}
                placeholder="Projects"
                optionFilterProp="label"
              />
            </Form.Item>
          )}
          {
            hasTimeTrackingGridExportColumns
            && (
              <CustomExportTimeTrackingFilters
                selectedProjects={selectedProjects}
                selectedCostcodes={selectedCostcodes}
                selectedTimeTypes={selectedTimeTypes}
                onFilterChange={onSelectChange}
              />
            )
          }

          {
            selectedExport?.type === 'forms'
            && (
              <CustomExportFormFilters
                divisionId={selectedExport?.divisionId}
                onFilterChange={onSelectChange}
                selectedCustomers={selectedCustomers}
                selectedEquipment={selectedEquipment}
                selectedBuckets={selectedBuckets}
              />
            )
          }

        </Form>
      </div>
      <div className="drawer-footer">
        <Row justify="end" gutter={10}>
          <div style={{ marginRight: 'auto' }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {
              filterView
                && (
                  <OnTraccrButton
                    title="Delete"
                    type="back"
                    onClick={onDelete}
                    style={{ marginLeft: 8 }}
                  />
                )
            }
          </div>
          <OnTraccrButton
            title={`${!filterView ? 'Create' : 'Save'} and Apply`}
            onClick={onSave}
            style={{ marginRight: 8 }}
          />
          <OnTraccrButton
            title="Apply"
            onClick={onApply}
          />
        </Row>
      </div>
      <SimpleTextInputModal
        title="Add Filter View"
        visible={isModalOpen}
        placeholder="Enter Filter View name"
        onClose={() => setIsModalOpen(false)}
        onSave={addNewFilterView}
      />
    </>
  );
}

CustomExportFilterTab.propTypes = {
  onClose: PropTypes.func.isRequired,
  filterView: PropTypes.shape({
    filterViewName: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.string),
    costcodes: PropTypes.arrayOf(PropTypes.string),
  }),
  setActiveTab: PropTypes.func.isRequired,
  selectedExport: PropTypes.shape({
    divisionId: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onApplyFilter: PropTypes.func.isRequired,
  activeFilters: PropTypes.shape({
    filterView: PropTypes.string,
  }),
};

CustomExportFilterTab.defaultProps = {
  filterView: null,
  selectedExport: {},
  activeFilters: {},
};
